import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { PopupMenu } from '@kerfjs/ui/popup-menu';
import { Toolbar } from '@kerfjs/ui/toolbar';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import type { SafeHtml } from 'kerfjs';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  MessageSquareText,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  X,
} from 'lucide';

import { menuProps } from '../../../../../lib/action-menu.js';
import type { GalleryPreview } from './gallery-types.js';

export function GalleryToolbar({
  active,
  filename,
  documentId,
  index,
  count,
  readOnly,
  library = false,
}: {
  active: GalleryPreview;
  filename: string;
  documentId: string;
  index: number;
  count: number;
  readOnly: boolean;
  library?: boolean;
}): SafeHtml {
  return (
    <div class="gallery-toolbar document-preview-toolbar">
      <span class="document-preview-mobile-filename">{filename}</span>
      <Toolbar
        dividerSides=""
        leading={
          <ToolbarControlGroup
            label="Image Filename"
            tone="dark"
            appearance="borderless"
            content="text"
            visibility="hide-collapsed-tiny"
          >
            <span class="document-preview-filename" title={filename}>
              {filename}
            </span>
          </ToolbarControlGroup>
        }
        trailing={
          <>
            <ToolbarControlGroup label="Image Navigation" tone="dark">
              <button
                type="button"
                data-action="document-preview-shift"
                data-item-id="-1"
                aria-label="Previous Image"
                title="Previous image"
                disabled={count < 2}
              >
                <LucideIcon icon={ChevronLeft} name="" />
              </button>
              <span class="document-preview-count">
                {String(index + 1)} / {String(count)}
              </span>
              <button
                type="button"
                data-action="document-preview-shift"
                data-item-id="1"
                aria-label="Next Image"
                title="Next image"
                disabled={count < 2}
              >
                <LucideIcon icon={ChevronRight} name="" />
              </button>
            </ToolbarControlGroup>
            <ToolbarControlGroup label="Image Actions" tone="dark" single>
              <button
                type="button"
                data-action="document-download"
                data-item-id={documentId}
                aria-label="Download Image"
                title="Download image"
              >
                <LucideIcon icon={Download} name="" />
              </button>
            </ToolbarControlGroup>
            {library ? null : (
              <ToolbarControlGroup label="Annotation and File Actions" tone="dark">
                <button
                  type="button"
                  data-action="document-preview-annotate"
                  aria-label="Annotate Image"
                  title="Annotate image"
                  aria-pressed={String(active.annotating === true)}
                  disabled={active.annotationsLoading === true}
                >
                  <LucideIcon icon={MessageSquareText} name="" />
                </button>
                <PopupMenu
                  {...menuProps(
                    {
                      label: 'More Image Actions',
                      icon: <LucideIcon icon={MoreHorizontal} name="" />,
                      rootAttributes: { 'data-gallery-actions': '' },
                    },
                    [
                      {
                        label: 'Copy File Reference',
                        icon: <LucideIcon icon={Copy} name="" />,
                        action: 'gallery-copy-reference',
                      },
                      ...(readOnly
                        ? []
                        : [
                            {
                              label: 'Replace with New Version',
                              icon: <LucideIcon icon={RefreshCw} name="" />,
                              action: 'gallery-replace-open',
                            },
                            {
                              label: 'Delete File',
                              icon: <LucideIcon icon={Trash2} name="" />,
                              action: 'gallery-delete',
                            },
                          ]),
                    ],
                  )}
                />
                <input type="file" hidden data-gallery-replace-input accept="image/*" />
              </ToolbarControlGroup>
            )}
            <ToolbarControlGroup label="Close Gallery" tone="dark" single>
              <button
                type="button"
                data-action="document-preview-close"
                aria-label="Close Image Gallery"
                title="Close image gallery"
              >
                <LucideIcon icon={X} name="" />
              </button>
            </ToolbarControlGroup>
          </>
        }
      />
    </div>
  );
}
